import React from 'react';
import { KoText } from 'packages';

// ── RELEASE LIFECYCLE STAGES ────────────────────────────────────
export const RELEASE_LIFECYCLE = {
  DRAFT: 'draft',
  IN_DEVELOPMENT: 'in_development',
  CODE_REVIEW: 'code_review',
  IN_QA: 'in_qa',
  IN_UAT: 'in_uat',
  RELEASE_GATE: 'release_gate',
  DEPLOYING: 'deploying',
  MONITORING: 'monitoring',
  CLOSED: 'closed',
  ROLLED_BACK: 'rolled_back'
};

export const LIFECYCLE_STAGES = [
  { key: 'draft', label: 'Draft', icon: 'FileOutlined', color: 'default' },
  { key: 'in_development', label: 'In Development', icon: 'CodeOutlined', color: 'processing' },
  { key: 'code_review', label: 'Code Review', icon: 'PullRequestOutlined', color: 'blue' },
  { key: 'in_qa', label: 'QA Validation', icon: 'CheckCircleOutlined', color: 'orange' },
  { key: 'in_uat', label: 'UAT Sign-off', icon: 'FileDoneOutlined', color: 'warning' },
  { key: 'release_gate', label: 'Release Gate', icon: 'LockOutlined', color: 'purple' },
  { key: 'deploying', label: 'Deploying', icon: 'RocketOutlined', color: 'cyan' },
  { key: 'monitoring', label: 'Monitoring', icon: 'DashboardOutlined', color: 'geekblue' },
  { key: 'closed', label: 'Closed', icon: 'CheckCircleOutlined', color: 'success' },
  { key: 'rolled_back', label: 'Rolled Back', icon: 'RollbackOutlined', color: 'error' }
];

export const LIFECYCLE_STAGE_MAP = LIFECYCLE_STAGES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

// ── RELEASE CLASSIFICATION ──────────────────────────────────────
export const RELEASE_CLASSIFICATION = [
  { label: 'Major', value: 'major', description: 'Breaking changes — Executive approval', color: 'error' },
  { label: 'Minor', value: 'minor', description: 'New features — Product approval', color: 'warning' },
  { label: 'Patch', value: 'patch', description: 'Bug fixes — Engineering approval', color: 'processing' },
  { label: 'Hotfix', value: 'hotfix', description: 'Production emergency — Fast-track', color: 'magenta' }
];

// ── PR STATUS ───────────────────────────────────────────────────
export const PR_STATUS = {
  DRAFT: 'draft',
  OPEN: 'open',
  IN_REVIEW: 'in_review',
  CHANGES_REQUESTED: 'changes_requested',
  APPROVED: 'approved',
  MERGED: 'merged',
  CLOSED: 'closed'
};

export const PR_STATUS_CONFIG = {
  draft: { color: 'default', label: 'Draft', icon: 'DraftOutlined' },
  open: { color: 'blue', label: 'Open', icon: 'PullRequestOutlined' },
  in_review: { color: 'processing', label: 'In Review', icon: 'EyeOutlined' },
  changes_requested: { color: 'error', label: 'Changes Requested', icon: 'CloseCircleOutlined' },
  approved: { color: 'success', label: 'Approved', icon: 'CheckCircleOutlined' },
  merged: { color: 'purple', label: 'Merged', icon: 'MergeOutlined' },
  closed: { color: 'default', label: 'Closed', icon: 'MinusCircleOutlined' }
};

// ── QA STATUS ───────────────────────────────────────────────────
export const QA_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  PASSED: 'passed',
  FAILED: 'failed'
};

export const QA_RESULT_CONFIG = {
  passed: { color: 'success', label: 'Passed' },
  failed: { color: 'error', label: 'Failed' },
  in_progress: { color: 'processing', label: 'In Progress' },
  not_started: { color: 'default', label: 'Not Started' }
};

// ── UAT STATUS ──────────────────────────────────────────────────
export const UAT_STATUS = {
  PENDING: 'pending',
  SIGNED_OFF: 'signed_off',
  REJECTED: 'rejected'
};

// ── TICKET TYPE ─────────────────────────────────────────────────
export const TICKET_TYPE = {
  FEATURE: 'feature',
  BUG: 'bug',
  IMPROVEMENT: 'improvement',
  TASK: 'task'
};

// ── GIT PROVIDERS ───────────────────────────────────────────────
export const GIT_PROVIDERS = [
  { label: 'GitHub', value: 'github', icon: 'GithubOutlined' },
  { label: 'GitLab', value: 'gitlab', icon: 'GitlabOutlined' },
  { label: 'Bitbucket', value: 'bitbucket', icon: 'BitbucketOutlined' }
];

// ── ENVIRONMENT PROMOTION ───────────────────────────────────────
export const ENVIRONMENT_OPTIONS = [
  { label: 'Development', value: 'development' },
  { label: 'QA', value: 'qa' },
  { label: 'UAT', value: 'uat' },
  { label: 'Production', value: 'production' }
];

export const ENVIRONMENT_PROMOTION = [
  { key: 'development', label: 'Dev', source: 'develop', approval: 'None' },
  { key: 'qa', label: 'QA', source: 'release/*', approval: 'Dev Lead' },
  { key: 'uat', label: 'UAT', source: 'release/*', approval: 'QA Approved' },
  { key: 'production', label: 'Production', source: 'main', approval: 'QA + UAT + Release Gate' }
];

// ── TEAMS ───────────────────────────────────────────────────────
export const RELEASE_TEAM_OPTIONS = [
  { label: 'Mobile App', value: 'mobile_app' },
  { label: 'API Team', value: 'api' },
  { label: 'Desktop App', value: 'desktop_app' },
  { label: 'Web App', value: 'web_app' },
  { label: 'Backend Services', value: 'backend' },
  { label: 'DevOps / Infrastructure', value: 'devops' },
  { label: 'Data / Analytics', value: 'data' },
  { label: 'Other', value: 'other' }
];

// ── TEAM PARTICIPATION STATUS ───────────────────────────────────
export const TEAM_STATUS_OPTIONS = [
  { label: 'Active', value: 'active', color: 'processing' },
  { label: 'Completed', value: 'completed', color: 'success' },
  { label: 'Blocked', value: 'blocked', color: 'error' }
];

// ── RISK LEVELS ─────────────────────────────────────────────────
export const RISK_LEVEL_OPTIONS = [
  { label: 'Low', value: 'low', color: 'success' },
  { label: 'Medium', value: 'medium', color: 'warning' },
  { label: 'High', value: 'high', color: 'orange' },
  { label: 'Critical', value: 'critical', color: 'error' }
];

// ── MIGRATION CLASSIFICATION ────────────────────────────────────
export const MIGRATION_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Backward Compatible', value: 'backward_compatible' },
  { label: 'Breaking', value: 'breaking' }
];

// ── QA VALIDATION TYPES ─────────────────────────────────────────
export const QA_TYPE_OPTIONS = [
  { label: 'Regression', value: 'regression' },
  { label: 'Cross-repo Integration', value: 'cross_repo' },
  { label: 'Performance', value: 'performance' },
  { label: 'API Contract', value: 'api_contract' },
  { label: 'DB Migration Dry Run', value: 'db_migration' },
  { label: 'Security', value: 'security' },
  { label: 'Backward Compatibility', value: 'backward_compat' },
  { label: 'Smoke Test', value: 'smoke' }
];

// ── SEVERITY OPTIONS ────────────────────────────────────────────
export const SEVERITY_OPTIONS = [
  { label: 'Sev 1 — System Outage', value: 'sev1', action: 'Immediate rollback', color: 'error' },
  { label: 'Sev 2 — Major Feature Broken', value: 'sev2', action: 'Hotfix', color: 'orange' },
  { label: 'Sev 3 — Minor Defect', value: 'sev3', action: 'Patch', color: 'warning' },
  { label: 'Sev 4 — Cosmetic', value: 'sev4', action: 'Backlog', color: 'default' }
];

// ── RELEASE GATE CHECKLIST ──────────────────────────────────────
export const RELEASE_GATE_ITEMS = [
  { key: 'qa_approved', label: 'QA Approved', icon: 'CheckCircleOutlined' },
  { key: 'uat_approved', label: 'UAT Approved', icon: 'FileDoneOutlined' },
  { key: 'security_scan', label: 'Security Scan Clean', icon: 'LockOutlined' },
  { key: 'migration_validated', label: 'Migration Validated', icon: 'DatabaseOutlined' },
  { key: 'rollback_plan', label: 'Rollback Plan Verified', icon: 'RollbackOutlined' },
  { key: 'feature_flags', label: 'Feature Flags Configured', icon: 'FlagOutlined' },
  { key: 'monitoring_prepared', label: 'Monitoring Prepared', icon: 'DashboardOutlined' },
  { key: 'support_briefed', label: 'Support Briefed', icon: 'TeamOutlined' },
  { key: 'release_notes', label: 'Release Notes Finalized', icon: 'FileTextOutlined' },
  { key: 'incident_response', label: 'Incident Response Plan Ready', icon: 'AlertOutlined' }
];

// ── GATE APPROVER ROLES ─────────────────────────────────────────
export const GATE_APPROVER_ROLES = [
  { key: 'engineering_lead', label: 'Engineering Lead', icon: 'CodeOutlined' },
  { key: 'qa_lead', label: 'QA Lead', icon: 'CheckCircleOutlined' },
  { key: 'product_owner', label: 'Product Owner', icon: 'UserOutlined' }
];

// ── PR GOVERNANCE CHECKS ────────────────────────────────────────
export const PR_GOVERNANCE_CHECKS = [
  { key: 'linked_ticket', label: 'Linked Ticket' },
  { key: 'linked_release', label: 'Linked Release' },
  { key: 'description', label: 'Description Provided' },
  { key: 'risk_note', label: 'Risk Impact Note' },
  { key: 'migration_note', label: 'Migration Note' },
  { key: 'feature_flag_ref', label: 'Feature Flag Reference' }
];

export const PR_AUTOMATED_CHECKS = [
  { key: 'reviewer_approvals', label: '2 Reviewer Approvals' },
  { key: 'ci_passing', label: 'CI Passing' },
  { key: 'unit_tests', label: 'Unit Tests Passing' },
  { key: 'coverage_threshold', label: 'Coverage Threshold Met' },
  { key: 'static_analysis', label: 'Static Code Analysis Clean' },
  { key: 'dependency_scan', label: 'Dependency Vulnerability Scan' },
  { key: 'secrets_scan', label: 'Secrets Scan' },
  { key: 'license_compliance', label: 'License Compliance' }
];

// ── UAT VALIDATION CATEGORIES ───────────────────────────────────
export const UAT_VALIDATION_CATEGORIES = [
  { key: 'business_workflow', label: 'Business Workflow Integrity' },
  { key: 'reporting', label: 'Reporting Correctness' },
  { key: 'data_accuracy', label: 'Data Accuracy' },
  { key: 'ui_validation', label: 'UI Validation' },
  { key: 'customer_impact', label: 'Customer Impact Review' }
];

// ── PR STATUS OPTIONS ───────────────────────────────────────────
export const PR_STATUS_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Open', value: 'open' },
  { label: 'In Review', value: 'in_review' },
  { label: 'Changes Requested', value: 'changes_requested' },
  { label: 'Approved', value: 'approved' },
  { label: 'Merged', value: 'merged' }
];

// ── RACI MATRIX ─────────────────────────────────────────────────
export const RACI_MATRIX = [
  { stage: 'Dev', responsible: 'Developer', accountable: 'Eng Lead', consulted: 'QA', informed: 'Product' },
  { stage: 'QA', responsible: 'QA Team', accountable: 'QA Lead', consulted: 'Dev', informed: 'Product' },
  { stage: 'UAT', responsible: 'Product', accountable: 'Business Owner', consulted: 'QA', informed: 'Support' },
  { stage: 'Deploy', responsible: 'DevOps', accountable: 'Eng Lead', consulted: 'QA', informed: 'All' },
  { stage: 'Rollback', responsible: 'DevOps', accountable: 'Eng Lead', consulted: 'QA', informed: 'All' }
];

// ── DEFAULT RELEASE FLOW ───────────────────────────────────────
export const DEFAULT_RELEASE_FLOW = [
  { label: 'Pull Requests', value: 1, background: 'rgba(24,144,255,0.15)', color: '#1890ff', nextStatuses: [{ label: 'QA Testing', value: 2 }] },
  { label: 'QA Testing', value: 2, background: 'rgba(82,196,26,0.15)', color: '#52c41a', nextStatuses: [{ label: 'UAT Sign-off', value: 3 }] },
  { label: 'UAT Sign-off', value: 3, background: 'rgba(250,173,20,0.15)', color: '#faad14', nextStatuses: [{ label: 'Release Gate', value: 4 }] },
  { label: 'Release Gate', value: 4, background: 'rgba(114,46,209,0.15)', color: '#722ed1', nextStatuses: [{ label: 'Release Notes', value: 5 }] },
  { label: 'Release Notes', value: 5, background: 'rgba(19,194,194,0.15)', color: '#13c2c2', nextStatuses: [] }
];

// ── TABLE COLUMN DEFINITIONS ────────────────────────────────────

export const COLUMNS_RELEASES = [
  { label: 'Version', id: 'version', key: 'version', width: 110, render: (text) => <KoText className='monoText' text={text} /> },
  { label: 'Name', id: 'name', key: 'name', width: 180 },
  { label: 'Classification', id: 'classification', key: 'classification', width: 110 },
  { label: 'Teams', id: 'participatingTeams', key: 'participatingTeams', width: 200 },
  { label: 'Environment', id: 'environment', key: 'environment', width: 120 },
  { label: 'Stage', id: 'stage', key: 'stage', width: 140 },
  { label: 'QA', id: 'qaStatus', key: 'qaStatus', width: 90 },
  { label: 'UAT', id: 'uatStatus', key: 'uatStatus', width: 90 },
  { label: 'Risk', id: 'riskLevel', key: 'riskLevel', width: 90 },
  { label: 'Target Date', id: 'targetDate', key: 'targetDate', width: 120 },
  { label: 'Owner', id: 'owner', key: 'owner', width: 120 }
];

export const COLUMNS_PRS = [
  { label: 'PR', id: 'prNumber', key: 'prNumber', width: 70, render: (text) => <KoText className='monoText' text={`#${text}`} /> },
  { label: 'Title', id: 'title', key: 'title' },
  { label: 'Ticket', id: 'ticketId', key: 'ticketId', width: 120 },
  { label: 'Author', id: 'author', key: 'author', width: 140 },
  { label: 'Reviewers', id: 'reviewers', key: 'reviewers', width: 140 },
  { label: 'Checks', id: 'checksStatus', key: 'checksStatus', width: 100 },
  { label: 'Files', id: 'filesChanged', key: 'filesChanged', width: 70 },
  { label: 'Status', id: 'status', key: 'status', width: 150 }
];

export const COLUMNS_TICKETS = [
  { label: 'Ticket ID', id: 'ticketId', key: 'ticketId', width: 120 },
  { label: 'Title', id: 'title', key: 'title' },
  { label: 'Type', id: 'type', key: 'type', width: 110 },
  { label: 'Assignee', id: 'assignee', key: 'assignee', width: 150 },
  { label: 'PR Status', id: 'prStatus', key: 'prStatus', width: 130 },
  { label: 'QA', id: 'qaStatus', key: 'qaStatus', width: 100 },
  { label: 'UAT', id: 'uatStatus', key: 'uatStatus', width: 100 },
  { label: 'Status', id: 'status', key: 'status', width: 130 }
];

export const COLUMNS_QA = [
  { label: 'Ticket', id: 'ticketId', key: 'ticketId', width: 120 },
  { label: 'Test Case', id: 'title', key: 'title' },
  { label: 'Type', id: 'type', key: 'type', width: 140 },
  { label: 'Tester', id: 'tester', key: 'tester', width: 140 },
  { label: 'Environment', id: 'environment', key: 'environment', width: 120 },
  { label: 'Result', id: 'result', key: 'result', width: 130 }
];
